import React, { useMemo } from 'react';

import _, { isEmpty, isNil } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';

import { youthItemsSel } from '@modules/advancement/common';
import {
  awardsByProgram,
  notApprovableRankIds,
} from '@modules/advancement/constants';
import {
  selectedRowKeysSel,
  setSelectedRowKeys,
} from '@modules/advancement/pendingItems/duck';
import { usePendingItemsRequirementsQuery } from '@modules/advancement/pendingItems/hooks';
import {
  GroupedPendingItem,
  GroupedPendingItemChild,
  PendingItem,
  TableClickActions,
} from '@modules/advancement/pendingItems/types';
import {
  groupPendingItems,
  isYouthInUserSubunitChecker,
  sortChildrenItems,
} from '@modules/advancement/pendingItems/utils';
import {
  achieveFirstClassAndAbove,
  searchOnItems,
} from '@modules/advancement/purchaseOrder/utils';
import { ApproveAdvancementsButton } from '@modules/progress/approveAdvancements';
import '@modules/shared';
import { PaginationTotal } from '@modules/shared/components';
import Table from '@modules/shared/components/Table';
import {
  advancementTypes,
  programNames,
  programTypeName,
  ranksRequirementIdsWithoutApproveOption,
} from '@modules/shared/constants';
import {
  hasNewPermissionSel,
  selectedOrganizationSel,
  shouldFilterUsersBySubunitsSel,
} from '@modules/shared/duck/selectors';
import { toggleArrayItem } from '@modules/utils';
import { newPermissions } from '@shared/newPermissions';

import { userSubUnitMembersSel } from '../../../../packRoster/duck';
import PendingItemsCustomRow from './PendingItemsCustomRow';
import styles from './PendingItemsTable.less';
import { groupedColumns } from './PendingRequirementsColumns';
import PendingRequirementsTableNotApprovable from './PendingRequirementsTableNotApprovable';

export interface PendingRequirementsTableProps {
  items?: GroupedPendingItem[];

  rowSelection?: {
    selectedRowKeys: string[];
    onChange: (rowKeys: string) => void;
    onSelectAll: (isSelectAll: boolean) => void;
  };

  onGoToYouthProfile: TableClickActions['onGoToYouthProfile'];
  onGoToAdultProfile: TableClickActions['onGoToAdultProfile'];
  searchQuery?: string;
}

export const PendingRequirementsTableOriginal: React.FC<
  PendingRequirementsTableProps
> = props => {
  const { onGoToAdultProfile, onGoToYouthProfile, searchQuery = '' } = props;
  const dispatch = useDispatch();

  const currentOrg = useSelector(selectedOrganizationSel);
  const rosterItems = useSelector(youthItemsSel);
  const selectedRowKeys = useSelector(selectedRowKeysSel);
  const userSubUnitMembers = useSelector(userSubUnitMembersSel);
  const shouldFilterUsersBySubunits = useSelector(
    shouldFilterUsersBySubunitsSel,
  );
  const canApprove = useSelector(state =>
    hasNewPermissionSel(state, newPermissions.APPROVE_ADVANCEMENTS),
  );

  const areAnyAdvancementsSelected = selectedRowKeys.length > 0;

  const {
    data: pendingItemsData,
    isLoading,
    isFetching,
  } = usePendingItemsRequirementsQuery();

  const userSubUnitUsersMIDs: number[] = useMemo(
    () =>
      userSubUnitMembers
        .map(member => member.memberId)
        .filter(memberId => !isNil(memberId)) as number[],
    [userSubUnitMembers],
  );

  const isInSubUnit = (record: GroupedPendingItem | PendingItem) =>
    isYouthInUserSubunitChecker(
      shouldFilterUsersBySubunits,
      userSubUnitUsersMIDs,
      record.memberId,
    );

  const isNotApprovable = (
    record: GroupedPendingItem | PendingItem | GroupedPendingItemChild,
  ) => {
    if (
      (record as PendingItem).program !== undefined &&
      notApprovableRankIds.includes((record as PendingItem).id)
    ) {
      return true;
    }

    if (
      (record as GroupedPendingItem).totalAmount !== undefined &&
      (record as GroupedPendingItem).totalAmount === 0
    ) {
      return true;
    }

    if (
      ranksRequirementIdsWithoutApproveOption.includes(
        (record as PendingItem).id,
      )
    ) {
      return true;
    }

    return false;
  };

  const items = useMemo(() => {
    if (!pendingItemsData) return [];

    const values = groupPendingItems(pendingItemsData);

    const sortedData = sortChildrenItems(values);

    return sortedData;
  }, [pendingItemsData]);

  const selectedAdvancements = useMemo(() => {
    const setKeys = new Set(selectedRowKeys);

    return items
      .flatMap(({ children }) => children)
      .filter(item => setKeys.has(item.key))
      .filter(Boolean);
  }, [items, selectedRowKeys]);

  const addNeededFields = items.map(youth => ({
    ...youth,
    firstClassAndAbove: achieveFirstClassAndAbove(youth.memberId, rosterItems),
    children: youth.children.map(item => ({
      ...item,
      isUserInSubUnit: isYouthInUserSubunitChecker(
        shouldFilterUsersBySubunits,
        userSubUnitUsersMIDs,
        youth.memberId,
      ),
    })),
    totalApprovable:
      youth.children?.filter(item => !isNotApprovable(item)).length ?? 0,
  }));

  const approvableItemsWithChildren = addNeededFields
    .map(youth => {
      const filteredChildren = youth.children.filter(adv => {
        if (adv.advancementType === advancementTypes.AWARDS) {
          return awardsByProgram[currentOrg.program].has(
            Number(adv.advancementId),
          );
        }

        if (youth.firstClassAndAbove) {
          return (
            adv.program === currentOrg.programType ||
            adv.program === programNames.BOY_SCOUT
          );
        } else {
          return adv.program === currentOrg.programType;
        }
      });

      const itemTypes = Array.from(
        new Set(filteredChildren.map(item => item.advancementType)),
      );

      const description = itemTypes.map(_.startCase).join(', ');

      return {
        ...youth,
        description,
        children: filteredChildren,
      };
    })
    .filter(youth => youth.children.length > 0);

  const notApprovableItemsWithChildren = addNeededFields
    .map(youth => {
      let filteredChildren = [];

      if (youth.firstClassAndAbove) {
        filteredChildren = youth.children.filter(adv => {
          if (adv.advancementType === advancementTypes.AWARDS) {
            return !awardsByProgram[currentOrg.program].has(
              Number(adv.advancementId),
            );
          } else {
            return (
              adv.program !== currentOrg.programType &&
              adv.program !== programNames.BOY_SCOUT
            );
          }
        });
      } else {
        filteredChildren = youth.children.filter(adv => {
          if (adv.advancementType === advancementTypes.AWARDS) {
            return !awardsByProgram[currentOrg.program].has(
              Number(adv.advancementId),
            );
          } else {
            return adv.program !== currentOrg.programType;
          }
        });
      }
      const itemTypes = Array.from(
        new Set(filteredChildren.map(item => item.advancementType)),
      );
      const description = itemTypes.map(_.startCase).join(', ');

      return {
        ...youth,
        description,
        children: filteredChildren,
      };
    })
    .filter(youth => youth.children.length > 0);

  const searchedItems = searchOnItems(searchQuery, approvableItemsWithChildren);
  const showNotApprovableTable =
    currentOrg.programType !== programTypeName.CUB_SCOUT &&
    notApprovableItemsWithChildren.length > 0;

  const handleSelectRow = (record: GroupedPendingItem | PendingItem) => {
    if (!isInSubUnit(record)) return;
    const newSelectedKeys = toggleArrayItem(selectedRowKeys, record.key);
    dispatch(setSelectedRowKeys(newSelectedKeys));
  };

  const rowSelection = {
    selectedRowKeys: selectedRowKeys,
    getCheckboxProps: (record: GroupedPendingItem | PendingItem) => {
      const isGroup = 'groupId' in record;
      let hide = false;

      if (isGroup) {
        hide = (record?.totalApprovable ?? 0) === 0;
      } else {
        hide = isInSubUnit(record) && isNotApprovable(record);
      }

      const disable = !isInSubUnit(record) || isNotApprovable(record);

      return {
        disabled: disable,
        style: hide ? { display: 'none' } : {},
      };
    },
    onSelect(record: GroupedPendingItem | PendingItem, selected: boolean) {
      if (
        !isYouthInUserSubunitChecker(
          shouldFilterUsersBySubunits,
          userSubUnitUsersMIDs,
          record.memberId,
        )
      )
        return;
      const isGroup = 'groupId' in record;
      if (selected) {
        if (isGroup) {
          const selected = record.children
            .filter(item => !isNotApprovable(item))
            .map(item => item.key);
          dispatch(
            setSelectedRowKeys([...selectedRowKeys, ...selected, record.key]),
          );
        } else {
          dispatch(setSelectedRowKeys([...selectedRowKeys, record.key]));
        }
      } else if (isGroup) {
        const selectedItems = new Set<string | number>(selectedRowKeys);
        selectedItems.delete(record.key);
        record.children
          .filter(item => !isNotApprovable(item))
          .forEach(item => {
            selectedItems.delete(item.key);
          });
        dispatch(setSelectedRowKeys(Array.from(selectedItems)));
      } else {
        const currentSelectedKeys = new Set<string | number>(selectedRowKeys);
        currentSelectedKeys.delete(record.key);
        dispatch(setSelectedRowKeys(Array.from(currentSelectedKeys)));
      }
    },
    columnTitle: <></>,
  };

  const renderTitle = () => (
    <>
      {canApprove && (
        <ApproveAdvancementsButton
          className={styles.approveAdvancements}
          advancementStatus={null}
          disabled={!areAnyAdvancementsSelected}
          personsAdvancements={selectedAdvancements}
        />
      )}
    </>
  );

  return (
    <div className={styles.tableContainer}>
      <Table
        className={styles.table}
        dataSource={searchedItems}
        loading={isLoading || isFetching}
        rowKey="key"
        title={renderTitle}
        pagination={{
          showTotal: () => {
            const totalSelected = selectedRowKeys.filter(
              (item: string) => !item?.includes('group'),
            ).length;
            const totalNoGroup = approvableItemsWithChildren.flatMap(
              item => item.children,
            )?.length;

            return (
              <PaginationTotal total={totalNoGroup} selected={totalSelected} />
            );
          },
        }}
        rowSelection={rowSelection}
        columns={groupedColumns({
          onGoToYouthProfile,
          onGoToAdultProfile,
        })}
        rowClassName={(row: GroupedPendingItem) => {
          if (isEmpty(row?.children)) return styles.rowItem;
          else return styles.rowItemGroup;
        }}
        size="small"
        components={{
          body: {
            row: PendingItemsCustomRow,
          },
        }}
        onRow={(record: GroupedPendingItem | PendingItem) => ({
          onClick: () =>
            isNotApprovable(record) ? {} : handleSelectRow(record),
        })}
      />

      {showNotApprovableTable && (
        <PendingRequirementsTableNotApprovable
          onGoToYouthProfile={onGoToYouthProfile}
          onGoToAdultProfile={onGoToAdultProfile}
          isLoading={isLoading || isFetching}
          searchQuery={searchQuery}
          notApprovableItemsWithChildren={notApprovableItemsWithChildren}
        />
      )}
    </div>
  );
};

export const PendingRequirementsTable = React.memo(
  PendingRequirementsTableOriginal,
);
